<template>
    <div class="space-y-4">
        <div class="flex items-center gap-3 rounded-lg bg-white px-4 py-3 text-sm text-gray-600 shadow-sm ring-1 ring-black/5">
            <div class="flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-50">
                <UIcon name="i-ph-globe-hemisphere-east-duotone" class="size-5 text-brand-600" />
            </div>
            <p>
                Times are in <span class="font-semibold text-gray-800">{{ timezone }}</span>.
                Each session is <span class="font-semibold text-gray-800">{{ durationMin }} min</span>
                + <span class="font-semibold text-gray-800">{{ bufferMin }} min</span> buffer.
            </p>
        </div>

        <div class="grid items-start gap-4 xl:grid-cols-2">
            <div class="space-y-4">
                <div class="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                    <div class="flex items-center justify-between border-b border-gray-300 px-6 py-3">
                        <h3 class="text-lg font-bold text-gray-700">Weekly hours</h3>
                        <span class="text-xs text-gray-500">Repeats every week</span>
                    </div>

                    <div v-if="loading" class="space-y-3 px-6 py-5">
                        <USkeleton v-for="n in 7" :key="n" class="h-9" />
                    </div>

                    <ul v-else class="divide-y divide-gray-100">
                        <li v-for="day in WEEKDAYS_MONDAY_FIRST" :key="day.value" class="flex items-start gap-4 px-6 py-3">
                            <span class="w-24 flex-shrink-0 pt-1.5 text-sm font-medium" :class="rulesByDay[day.value].length ? 'text-gray-800' : 'text-gray-400'">
                                {{ day.label }}
                            </span>

                            <div class="min-w-0 flex-1 space-y-2">
                                <div v-if="rulesByDay[day.value].length" class="flex flex-wrap gap-2">
                                    <div
                                        v-for="rule in rulesByDay[day.value]"
                                        :key="rule.id"
                                        class="inline-flex items-center gap-1 rounded-md bg-brand-50 py-1 pl-2.5 pr-1 ring-1 ring-inset ring-brand-100"
                                    >
                                        <div class="leading-tight">
                                            <p class="text-sm font-semibold tabular-nums text-brand-800">
                                                {{ formatWindowTime(rule.start_time) }} – {{ formatWindowTime(rule.end_time) }}
                                            </p>
                                            <p class="text-[11px]" :class="windowHint(rule).warn ? 'text-amber-600' : 'text-brand-600/80'">
                                                {{ windowHint(rule).text }}
                                            </p>
                                        </div>
                                        <UButton
                                            @click="removeRule(rule)"
                                            :loading="removing === rule.id"
                                            :aria-label="`Remove ${day.label} ${formatWindowTime(rule.start_time)} – ${formatWindowTime(rule.end_time)}`"
                                            color="gray"
                                            variant="ghost"
                                            size="2xs"
                                            icon="i-ph-x-bold"
                                            :ui="{ color: { gray: { ghost: 'text-brand-700/60 hover:text-red-600 hover:bg-white/70' } } }"
                                        />
                                    </div>
                                </div>
                                <p v-else-if="adding?.weekday !== day.value" class="pt-1.5 text-sm text-gray-400">Unavailable</p>

                                <form v-if="adding?.weekday === day.value" @submit.prevent="addRule()" class="space-y-1.5">
                                    <div class="flex flex-wrap items-center gap-2">
                                        <UInput v-model="adding.start" type="time" size="sm" class="w-28" aria-label="Start time" autofocus />
                                        <span class="text-sm text-gray-400">to</span>
                                        <UInput v-if="!adding.untilMidnight" v-model="adding.end" type="time" size="sm" class="w-28" aria-label="End time" />
                                        <span v-else class="inline-flex h-8 w-28 items-center rounded-md bg-gray-50 px-2.5 text-sm text-gray-700 ring-1 ring-inset ring-gray-200">Midnight</span>
                                        <UCheckbox v-model="adding.untilMidnight" label="Until midnight" />
                                        <div class="flex items-center gap-1.5">
                                            <UButton type="submit" :loading="saving" color="black" size="sm">Add</UButton>
                                            <UButton @click="adding = undefined" color="gray" variant="ghost" size="sm">Cancel</UButton>
                                        </div>
                                    </div>
                                    <p v-if="adding.error" class="flex items-center gap-1 text-xs font-medium text-red-600">
                                        <UIcon name="i-ph-warning-circle-duotone" class="size-4" />
                                        {{ adding.error }}
                                    </p>
                                </form>
                            </div>

                            <UButton
                                v-if="adding?.weekday !== day.value"
                                @click="startAdding(day.value)"
                                color="gray"
                                variant="ghost"
                                size="xs"
                                icon="i-ph-plus-bold"
                                class="flex-shrink-0"
                            >
                                Add window
                            </UButton>
                        </li>
                    </ul>
                </div>

                <div class="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                    <div class="flex items-center justify-between border-b border-gray-300 px-6 py-3">
                        <h3 class="text-lg font-bold text-gray-700">Exceptions</h3>
                        <UButton @click="openException()" color="white" size="xs" icon="i-ph-plus-bold">Add exception</UButton>
                    </div>

                    <div v-if="loading" class="space-y-3 px-6 py-5">
                        <USkeleton v-for="n in 2" :key="n" class="h-10" />
                    </div>

                    <div v-else-if="!exceptions.length" class="flex flex-col items-center px-6 py-8 text-center">
                        <UIcon name="i-ph-calendar-blank-duotone" class="size-7 text-gray-300" />
                        <p class="mt-2 text-sm font-medium text-gray-600">No upcoming exceptions</p>
                        <p class="mt-0.5 text-xs text-gray-500">Block time off, or open extra hours on a specific date.</p>
                    </div>

                    <ul v-else class="divide-y divide-gray-100">
                        <li v-for="exception in exceptions" :key="exception.id" class="flex items-start gap-3 px-6 py-3">
                            <div
                                class="mt-0.5 flex size-8 flex-shrink-0 items-center justify-center rounded-full"
                                :class="exception.kind === 'blocked' ? 'bg-red-50' : 'bg-green-50'"
                            >
                                <UIcon
                                    :name="exception.kind === 'blocked' ? 'i-ph-prohibit-duotone' : 'i-ph-calendar-plus-duotone'"
                                    class="size-4"
                                    :class="exception.kind === 'blocked' ? 'text-red-500' : 'text-green-600'"
                                />
                            </div>
                            <div class="min-w-0 flex-1 space-y-1">
                                <div class="flex flex-wrap items-center gap-2">
                                    <span class="text-sm font-medium tabular-nums text-gray-800">
                                        {{ formatExceptionRange(exception.starts_at, exception.ends_at, timezone) }}
                                    </span>
                                    <UBadge
                                        :color="exception.kind === 'blocked' ? 'red' : 'green'"
                                        variant="subtle"
                                        size="xs"
                                        :label="exception.kind === 'blocked' ? 'Blocked' : 'Extra availability'"
                                    />
                                </div>
                                <p v-if="exception.note" class="truncate text-xs text-gray-500">{{ exception.note }}</p>
                            </div>
                            <UButton
                                @click="removeException(exception)"
                                :loading="removing === exception.id"
                                aria-label="Delete exception"
                                color="gray"
                                variant="ghost"
                                size="xs"
                                icon="i-ph-trash-duotone"
                            />
                        </li>
                    </ul>
                </div>
            </div>

            <TelemedicineSlotPreview :doctor-id="doctorId" :timezone="timezone" :days="14" :refresh-key="refreshKey" class="xl:sticky xl:top-4" />
        </div>

        <USlideover v-model="exceptionOpen">
            <UForm ref="exceptionForm" @submit="addException" :schema="exceptionSchema" :state="exceptionState" :validate-on="['submit', 'change']" class="h-full">
                <UCard class="flex h-full flex-col" :ui="{ body: { base: 'flex-1 overflow-y-auto' }, ring: '', divide: 'divide-y divide-gray-100' }">
                    <template #header>
                        <h3 class="text-base font-medium leading-6 text-gray-800">Add exception</h3>
                        <p class="mt-0.5 text-sm text-gray-500">Overrides the weekly hours for a specific period.</p>
                    </template>

                    <div class="space-y-6">
                        <UFormGroup name="kind" label="Type">
                            <div class="grid grid-cols-2 gap-2">
                                <button
                                    v-for="option in EXCEPTION_KINDS"
                                    :key="option.value"
                                    type="button"
                                    :aria-pressed="exceptionState.kind === option.value"
                                    @click="exceptionState.kind = option.value"
                                    class="rounded-lg p-3 text-left ring-1 transition"
                                    :class="exceptionState.kind === option.value ? option.active : 'ring-gray-200 hover:bg-gray-50'"
                                >
                                    <UIcon :name="option.icon" class="size-5" :class="option.iconClass" />
                                    <p class="mt-1.5 text-sm font-semibold text-gray-800">{{ option.label }}</p>
                                    <p class="mt-0.5 text-xs text-gray-500">{{ option.description }}</p>
                                </button>
                            </div>
                        </UFormGroup>
                        <UFormGroup name="starts_at" label="Starts" :help="`In ${timezone}`">
                            <UInput v-model="exceptionState.starts_at" type="datetime-local" size="lg" />
                        </UFormGroup>
                        <UFormGroup name="ends_at" label="Ends" :help="`In ${timezone}`">
                            <UInput v-model="exceptionState.ends_at" type="datetime-local" size="lg" />
                        </UFormGroup>
                        <UFormGroup name="note" label="Note" hint="Optional">
                            <UTextarea v-model="exceptionState.note" :rows="3" placeholder="e.g. Medical conference" />
                        </UFormGroup>
                    </div>

                    <template #footer>
                        <div class="flex items-center justify-end space-x-2">
                            <UButton @click="exceptionOpen = false" size="lg" color="white">Cancel</UButton>
                            <UButton type="submit" size="lg" :loading="saving" color="black">Add exception</UButton>
                        </div>
                    </template>
                </UCard>
            </UForm>
        </USlideover>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { z } from 'zod'
import type { AvailabilityException, AvailabilityRule } from '~/types/telemedicine'
import {
    WEEKDAYS_MONDAY_FIRST,
    formatExceptionRange,
    formatWindowTime,
    localInputToIso,
    scheduleErrorMessage,
    slotsPerWindow,
    validateRule,
    windowTouchesAnother,
} from '~/utils/telemedicineSchedule'

type ExceptionKind = AvailabilityException['kind']

const props = defineProps<{
    doctorId: string
    timezone: string
    durationMin: number
    bufferMin: number
}>()

const client = useSupabaseClient()
const toast = useToast()

const EXCEPTION_KINDS: { value: ExceptionKind, label: string, description: string, icon: string, iconClass: string, active: string }[] = [
    {
        value: 'blocked',
        label: 'Block time',
        description: 'No bookings during this period.',
        icon: 'i-ph-prohibit-duotone',
        iconClass: 'text-red-500',
        active: 'bg-red-50/60 ring-2 ring-red-400',
    },
    {
        value: 'extra',
        label: 'Extra availability',
        description: 'Open bookable time outside weekly hours.',
        icon: 'i-ph-calendar-plus-duotone',
        iconClass: 'text-green-600',
        active: 'bg-green-50/60 ring-2 ring-green-500',
    },
]

const loading = ref(true)
const saving = ref(false)
const removing = ref<string>()
const rules = ref<AvailabilityRule[]>([])
const exceptions = ref<AvailabilityException[]>([])
const refreshKey = ref(0)
const adding = ref<{ weekday: number, start: string, end: string, untilMidnight: boolean, error?: string }>()

const exceptionOpen = ref(false)
const exceptionForm = ref()
const exceptionState = ref<{ kind: ExceptionKind, starts_at: string, ends_at: string, note: string }>(emptyException())

const rulesByDay = computed(() => {
    const byDay: Record<number, AvailabilityRule[]> = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] }
    for (const rule of rules.value) byDay[rule.weekday]?.push(rule)
    return byDay
})

const exceptionSchema = z.object({
    kind: z.enum(['blocked', 'extra']),
    starts_at: z.string().refine((v) => !!localInputToIso(v, props.timezone), 'Pick a start date and time'),
    ends_at: z.string().refine((v) => !!localInputToIso(v, props.timezone), 'Pick an end date and time'),
    note: z.string().max(200, 'Keep the note under 200 characters'),
}).superRefine((value, ctx) => {
    const starts = localInputToIso(value.starts_at, props.timezone)
    const ends = localInputToIso(value.ends_at, props.timezone)
    if (!starts || !ends) return
    if (ends <= starts) ctx.addIssue({ code: 'custom', path: ['ends_at'], message: 'End must be after start' })
    else if (new Date(ends) <= new Date()) ctx.addIssue({ code: 'custom', path: ['ends_at'], message: 'End must be in the future' })
})

watch(() => props.doctorId, () => load(), { immediate: true })

async function load() {
    loading.value = true
    await Promise.all([fetchRules(), fetchExceptions()])
    loading.value = false
}

async function fetchRules() {
    const { data, error } = await client.from('availability_rules')
        .select('*')
        .eq('doctor_id', props.doctorId)
        .order('weekday')
        .order('start_time')
    if (error) {
        toast.add({ title: 'Could not load weekly hours', description: error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }
    rules.value = (data ?? []) as unknown as AvailabilityRule[]
}

async function fetchExceptions() {
    const { data, error } = await client.from('availability_exceptions')
        .select('*')
        .eq('doctor_id', props.doctorId)
        .gt('ends_at', new Date().toISOString())
        .order('starts_at')
    if (error) {
        toast.add({ title: 'Could not load exceptions', description: error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }
    exceptions.value = (data ?? []) as unknown as AvailabilityException[]
}

function windowHint(rule: AvailabilityRule) {
    const count = slotsPerWindow(rule.start_time, rule.end_time, props.durationMin, props.bufferMin)
    if (!count) return { text: 'Too short for a session', warn: true }
    const approx = windowTouchesAnother(rule, rules.value) ? '≈ ' : ''
    return { text: `${approx}${count} ${count === 1 ? 'slot' : 'slots'}`, warn: false }
}

function startAdding(weekday: number) {
    const last = rulesByDay.value[weekday].at(-1)
    const start = last ? formatWindowTime(last.end_time) : '09:00'
    if (start === 'Midnight') {
        adding.value = { weekday, start: '', end: '', untilMidnight: false }
        return
    }
    const startMinutes = Number(start.slice(0, 2)) * 60 + Number(start.slice(3, 5))
    const endMinutes = Math.min(startMinutes + 180, 1440)
    adding.value = {
        weekday,
        start,
        end: endMinutes >= 1440 ? '' : `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`,
        untilMidnight: endMinutes >= 1440,
    }
}

async function addRule() {
    const draft = adding.value
    if (!draft) return
    const end = draft.untilMidnight ? '24:00' : draft.end
    if (!draft.start || !end) {
        draft.error = 'Enter a start and end time'
        return
    }
    const candidate = { weekday: draft.weekday, start_time: draft.start, end_time: end }
    draft.error = validateRule(candidate, rules.value) ?? undefined
    if (draft.error) return

    saving.value = true
    const { error } = await client.from('availability_rules')
        .insert({ doctor_id: props.doctorId, ...candidate } as never)
    saving.value = false

    if (error) {
        draft.error = scheduleErrorMessage(error)
        toast.add({ title: 'Could not add the window', description: draft.error, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }

    adding.value = undefined
    await fetchRules()
    refreshKey.value++
}

async function removeRule(rule: AvailabilityRule) {
    removing.value = rule.id
    const { data, error } = await client.from('availability_rules')
        .delete()
        .eq('id', rule.id)
        .select('id')
    removing.value = undefined

    if (error || !data?.length) {
        toast.add({
            title: 'Could not remove the window',
            description: error ? scheduleErrorMessage(error) : 'It may already be gone. Reload the page and try again.',
            icon: 'i-ph-warning-duotone',
            color: 'red',
        })
        await fetchRules()
        return
    }

    await fetchRules()
    refreshKey.value++
}

function emptyException() {
    const tomorrow = dayjs().tz(props.timezone).add(1, 'day').format('YYYY-MM-DD')
    return { kind: 'blocked' as ExceptionKind, starts_at: `${tomorrow}T09:00`, ends_at: `${tomorrow}T17:00`, note: '' }
}

function openException() {
    exceptionState.value = emptyException()
    exceptionForm.value?.clear()
    exceptionOpen.value = true
}

async function addException() {
    const starts = localInputToIso(exceptionState.value.starts_at, props.timezone)
    const ends = localInputToIso(exceptionState.value.ends_at, props.timezone)
    if (!starts || !ends) return

    saving.value = true
    const { error } = await client.from('availability_exceptions')
        .insert({
            doctor_id: props.doctorId,
            kind: exceptionState.value.kind,
            starts_at: starts,
            ends_at: ends,
            note: exceptionState.value.note.trim() || null,
        } as never)
    saving.value = false

    if (error) {
        toast.add({ title: 'Could not add the exception', description: scheduleErrorMessage(error), icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }

    exceptionOpen.value = false
    toast.add({
        title: exceptionState.value.kind === 'blocked' ? 'Time blocked' : 'Extra availability added',
        icon: 'i-ph-check-circle-duotone',
        color: 'green',
    })
    await fetchExceptions()
    refreshKey.value++
}

async function removeException(exception: AvailabilityException) {
    removing.value = exception.id
    const { data, error } = await client.from('availability_exceptions')
        .delete()
        .eq('id', exception.id)
        .select('id')
    removing.value = undefined

    if (error || !data?.length) {
        toast.add({
            title: 'Could not delete the exception',
            description: error ? scheduleErrorMessage(error) : 'It may already be gone. Reload the page and try again.',
            icon: 'i-ph-warning-duotone',
            color: 'red',
        })
        await fetchExceptions()
        return
    }

    await fetchExceptions()
    refreshKey.value++
}
</script>
